import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./shared";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UI = {
  en: { already: "You're already on the list!", joined: "Welcome aboard — you're on the list!", error: "Something went wrong. Please try again.", success: "You're in", watch: "Watch your inbox.", explorer: "explorer" },
  hi: { already: "आप पहले से सूची में हैं!", joined: "स्वागत है — आप सूची में जुड़ गए!", error: "कुछ गड़बड़ हुई। कृपया फिर कोशिश करें।", success: "आप जुड़ गए", watch: "अपना इनबॉक्स देखते रहें।", explorer: "एक्सप्लोरर" },
};


export default function CTA() {
  const { lang, t } = useLang();
  const ui = UI[lang];
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [position, setPosition] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/early-access/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    try {
      const res = await fetch(`${API}/early-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPosition(data.count);
      setState("done");
      toast.success(data.already ? ui.already : ui.joined);
    } catch {
      setState("idle");
      toast.error(ui.error);
    }
  };

  return (
    <section id="cta" className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-iqpurple/15 blur-[120px]" />
        <div className="absolute -bottom-32 right-1/4 h-96 w-96 rounded-full bg-iqcyan/20 blur-[120px]" />
      </div>
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-28 sm:py-40 text-center">
        <Reveal>
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] text-balance">
            {t.ctaHeadingA} <span className="font-serifa italic font-normal text-iqblue">{t.ctaHeadingB}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base sm:text-lg text-ink/60 max-w-xl mx-auto leading-relaxed">
            {t.ctaSub(count || 0)}
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          {state === "done" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4"
              data-testid="cta-success-msg"
            >
              <PartyPopper className="h-5 w-5 text-iqamber" />
              <span className="font-semibold">{ui.success}{position ? ` — ${ui.explorer} #${position}` : ""}. {ui.watch}</span>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.ctaPlaceholder}
                data-testid="cta-email-input"
                className="flex-1 rounded-full border border-ink/20 bg-cream px-6 py-4 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35"
              />
              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="cta-submit-button"
                className="group flex items-center justify-center gap-2 rounded-full bg-ink text-paper font-semibold px-8 py-4 hover:bg-iqblue disabled:opacity-60 transition-colors duration-300"
              >
                {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>{t.ctaButton} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
